import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

// ✅ Fetch Consultations
export const fetchConsultations = createAsyncThunk(
  "consultations/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/consultation/consultations/");
      return response.data;
    } catch (error) {
      console.error("Error Fetching Consultations:", error);
      return rejectWithValue(error.response?.data || "Failed to fetch consultations");
    }
  }
);

// ✅ Create Consultation
export const createConsultation = createAsyncThunk(
  "consultations/create",
  async ({ patient, appointment }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/consultation/consultations/", {
        patient,
        appointment,
      });

      console.log("Consultation Created:", response.data); // Debugging

      return response.data; // ✅ Return the consultation object
    } catch (error) {
      console.error("Error Creating Consultation:", error);
      return rejectWithValue(error.response?.data || "Failed to create consultation");
    }
  }
);


export const completeConsultation = createAsyncThunk(
  "consultations/complete",
  async ({ consultationId, consultationData }, { rejectWithValue }) => {
    try {
      const formattedData = {
        ...consultationData,

        prescription: consultationData.prescription?.medications?.length
          ? {
              medications: consultationData.prescription.medications.map((med) => ({
                medication_name: med.medication || med.medication_name,
                dosage: med.dosage || "",
                frequency: med.frequency || "",
                instructions: med.instructions || "",
              })),
            }
          : { medications: [] },

        tests_ordered: consultationData.tests_ordered && consultationData.tests_ordered.length > 0
          ? consultationData.tests_ordered.map((test) => ({
              test_name: test.test_name || test.testName,
              instructions: test.instructions || "",
              status: test.status || "Pending",
            }))
          : [],

        referrals: consultationData.referrals && consultationData.referrals.length > 0
          ? consultationData.referrals.map((referral) => ({
              referred_to: referral.referred_to || referral.specialist,
              reason: referral.reason || "",
            }))
          : [],

        diagnosis: Array.isArray(consultationData.diagnoses)
          ? consultationData.diagnoses.join(", ")
          : consultationData.diagnosis || "",
      };

      console.log("🚀 Final Consultation Payload Sent to API:", JSON.stringify(formattedData, null, 2));

      const response = await axiosInstance.patch(
        `/consultation/consultations/${consultationId}/complete/`,
        formattedData,
        { headers: { "Content-Type": "application/json" } }
      );

      console.log("✅ Consultation Completed Response:", response.data);

      return response.data;
    } catch (error) {
      console.error("❌ Error Completing Consultation:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || "Failed to complete consultation");
    }
  }
);







const consultationSlice = createSlice({
  name: "consultations",
  initialState: {
    data: [], // ✅ Ensure initial state is an array
    selectedConsultation: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearSelectedConsultation: (state) => {
      state.selectedConsultation = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🔹 Fetch Consultations
      .addCase(fetchConsultations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConsultations.fulfilled, (state, action) => {
        state.loading = false;
        state.data = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchConsultations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🔹 Create Consultation
      .addCase(createConsultation.fulfilled, (state, action) => {
        if (!Array.isArray(state.data)) {
          state.data = [];
        }
        state.data.unshift(action.payload);
      })
      .addCase(createConsultation.rejected, (state, action) => {
        state.error = action.payload;
      })

      // 🔹 Complete Consultation
      .addCase(completeConsultation.fulfilled, (state, action) => {
        state.data = state.data.map((consultation) =>
          consultation.id === action.payload.id ? action.payload : consultation
        );
      })
      .addCase(completeConsultation.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearSelectedConsultation } = consultationSlice.actions;
export default consultationSlice.reducer;
